import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { WPW_MAIL_STATE_LABELS, type WpwRouteStalePackage } from "../types";

/**
 * Komunikat po wgraniu / przeliczeniu trasy: które pakiety WPW (i ich listy automatyczne)
 * przestały być aktualne, z numerem działki i właścicielem. Zamyka się wyłącznie
 * przyciskiem potwierdzenia (bez klikania w tło i bez Esc-a przez przypadek), żeby nikt nie
 * przeoczył, że listy stoją, dopóki pakiety nie zostaną przekompilowane albo wznowione ręcznie.
 */
export function WpwStaleAfterRouteModal({
  open,
  items,
  lettersHeld,
  onConfirm,
}: {
  open: boolean;
  items: WpwRouteStalePackage[];
  lettersHeld: number;
  onConfirm: () => void;
}) {
  const sentOutdated = items.filter((it) => it.sent_letter_outdated).length;
  return (
    <Modal open={open} onClose={onConfirm} title="Pakiety WPW nieaktualne po wgraniu trasy" size="lg">
      <div className="space-y-3 text-sm" data-testid="wpw-stale-after-route">
        <p className="flex items-start gap-2 text-[var(--color-text-primary)]">
          <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
          <span>
            Nowa trasa zmieniła przebieg na {items.length === 1 ? "1 działce" : `${items.length} działkach`} z gotowymi
            pakietami WPW. Te pakiety (terenowe i listowne) mają nieaktualną mapę i wymagają ponownej kompilacji.
            {lettersHeld > 0 && (
              <> Automatyczna wysyłka {lettersHeld === 1 ? "1 listu została wstrzymana" : `${lettersHeld} listów została wstrzymana`}
                {" "}— możesz ją wznowić ręcznie na karcie działki (Podstawa dostępu → Listy do właścicieli).</>
            )}
            {sentOutdated > 0 && (
              <> {sentOutdated === 1
                  ? "1 wysłany list może być nieaktualny — trasa na działce zmieniła się od wysyłki. Jeśli tak, koordynator oznaczy go"
                  : `Wysłane listy (${sentOutdated}) mogą być nieaktualne — trasa na działkach zmieniła się od wysyłki. Jeśli tak, koordynator oznaczy je`}
                {" "}jako nieaktualne na karcie działki (Podstawa dostępu → Listy), wtedy można wysłać kolejny list.</>
            )}
          </span>
        </p>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left border-b border-[var(--color-border)] font-medium uppercase tracking-wide text-[10px] text-[var(--color-text-muted)]">
              <th className="py-1 pr-3">Działka</th>
              <th className="py-1 pr-3">Właściciel</th>
              <th className="py-1 pr-3">WNU</th>
              <th className="py-1 pr-3">List automatyczny</th>
              <th className="py-1">Powód</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.application_id} className="border-b border-[var(--color-border-muted)] align-top" data-testid="wpw-stale-row">
                <td className="py-1 pr-3 font-mono whitespace-nowrap">{it.short_number || it.teryt}</td>
                <td className="py-1 pr-3">{it.owner || "—"}</td>
                <td className="py-1 pr-3 font-mono whitespace-nowrap">{it.number}</td>
                <td className="py-1 pr-3 whitespace-nowrap">
                  {it.letter_id
                    ? (it.letter_held ? "wstrzymany" : WPW_MAIL_STATE_LABELS[it.letter_state] ?? it.letter_state)
                    : "brak"}
                  {it.sent_letter_outdated && (
                    <span className="block text-amber-700 dark:text-amber-400" data-testid="wpw-stale-sent-outdated">
                      wysłany{it.sent_letter_print_order != null ? ` (#${it.sent_letter_print_order})` : ""} może być nieaktualny
                    </span>
                  )}
                </td>
                <td className="py-1 text-[var(--color-text-muted)]">{it.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end pt-1">
          <Button onClick={onConfirm} data-testid="wpw-stale-confirm">Potwierdzam, zamknij</Button>
        </div>
      </div>
    </Modal>
  );
}
